import { MakeLogicType, actions, connect, kea, key, listeners, path, props, reducers, selectors } from 'kea'
import { MutableRefObject } from 'react'

import { clamp } from 'lib/utils/numbers'
import {
    SessionRecordingPlayerLogicProps,
    sessionRecordingPlayerLogic,
} from 'scenes/session-recordings/player/sessionRecordingPlayerLogic'

import type { SessionPlayerData } from '../../../../types'
import { InteractEvent, ReactInteractEvent, THUMB_OFFSET, getXPos } from '../utils/playerUtils'

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface seekbarLogicValues {
    currentPlayerTime: number // sessionRecordingPlayerLogic
    sessionPlayerData: SessionPlayerData // sessionRecordingPlayerLogic
    cursorDiff: number
    endTimeMs: number
    isScrubbing: boolean
    isSeeking: boolean
    scrubbingTime: number
    scrubbingTimeSeconds: number
    slider: HTMLDivElement | null
    thumb: HTMLDivElement | null
    thumbLeftPos: number
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface seekbarLogicActions {
    endScrub: () => {
        value: true
    } // sessionRecordingPlayerLogic
    seekToTime: (
        timeInMilliseconds: number,
        forcePlay?: boolean | undefined
    ) => {
        forcePlay: boolean
        timeInMilliseconds: number
    } // sessionRecordingPlayerLogic
    setCurrentTimestamp: (timestamp: number) => {
        timestamp: number
    } // sessionRecordingPlayerLogic
    startScrub: () => {
        value: true
    } // sessionRecordingPlayerLogic
    debouncedSetTime: (time: number) => {
        time: number
    }
    endSeeking: () => {
        value: true
    }
    handleClick: (event: ReactInteractEvent) => {
        event: ReactInteractEvent
    }
    handleDown: (event: ReactInteractEvent) => {
        event: ReactInteractEvent
    }
    handleMove: (event: InteractEvent) => {
        event: InteractEvent
    }
    handleSeek: (
        newX: number,
        shouldSeek?: boolean
    ) => {
        newX: number
        shouldSeek: boolean
    }
    handleUp: (event: InteractEvent) => {
        event: InteractEvent
    }
    setCursorDiff: (cursorDiff: number) => {
        cursorDiff: number
    }
    setSlider: (ref: MutableRefObject<HTMLDivElement | null>) => {
        ref: MutableRefObject<HTMLDivElement | null>
    }
    setThumb: (ref: MutableRefObject<HTMLDivElement | null>) => {
        ref: MutableRefObject<HTMLDivElement | null>
    }
    setThumbLeftPos: (
        thumbLeftPos: number,
        shouldSeek: boolean
    ) => {
        shouldSeek: boolean
        thumbLeftPos: number
    }
}

// Generated by kea-typegen. Update if you're an agent, ignore if you're human.
export interface seekbarLogicMeta {
    key: string
    __keaTypeGenInternalSelectorTypes: {
        endTimeMs: (sessionPlayerData: SessionPlayerData) => number
        scrubbingTime: (
            thumbLeftPos: number,
            slider: HTMLDivElement | null,
            sessionPlayerData: SessionPlayerData
        ) => number
        scrubbingTimeSeconds: (scrubbingTime: number) => number
    }
}

export type seekbarLogicType = MakeLogicType<
    seekbarLogicValues,
    seekbarLogicActions,
    SessionRecordingPlayerLogicProps,
    seekbarLogicMeta
>

export const seekbarLogic = kea<seekbarLogicType>([
    path((key) => ['scenes', 'session-recordings', 'player', 'seekbarLogic', key]),
    props({} as SessionRecordingPlayerLogicProps),
    key((props: SessionRecordingPlayerLogicProps) => `${props.playerKey}-${props.sessionRecordingId}`),
    connect((props: SessionRecordingPlayerLogicProps) => ({
        values: [sessionRecordingPlayerLogic(props), ['sessionPlayerData', 'currentPlayerTime']],
        actions: [sessionRecordingPlayerLogic(props), ['seekToTime', 'startScrub', 'endScrub', 'setCurrentTimestamp']],
    })),
    actions({
        setThumbLeftPos: (thumbLeftPos: number, shouldSeek: boolean) => ({ thumbLeftPos, shouldSeek }),
        setCursorDiff: (cursorDiff: number) => ({ cursorDiff }),
        handleSeek: (newX: number, shouldSeek: boolean = true) => ({ newX, shouldSeek }),
        handleMove: (event: InteractEvent) => ({ event }),
        handleUp: (event: InteractEvent) => ({ event }),
        handleDown: (event: ReactInteractEvent) => ({ event }),
        handleClick: (event: ReactInteractEvent) => ({ event }),
        setSlider: (ref: MutableRefObject<HTMLDivElement | null>) => ({ ref }),
        setThumb: (ref: MutableRefObject<HTMLDivElement | null>) => ({ ref }),
        debouncedSetTime: (time: number) => ({ time }),
        endSeeking: true,
    }),
    reducers({
        thumbLeftPos: [
            -THUMB_OFFSET,
            {
                setThumbLeftPos: (_, { thumbLeftPos }) => {
                    // we receive this number to many decimal places, so we round it to 1 decimal place
                    // since otherwise we render dependent components
                    // thousands of times more than we need to
                    return parseFloat(thumbLeftPos.toFixed(1))
                },
            },
        ],
        cursorDiff: [
            0,
            {
                setCursorDiff: (_, { cursorDiff }) => cursorDiff,
            },
        ],
        slider: [
            null as HTMLDivElement | null,
            {
                setSlider: (_, { ref }) => ref.current,
            },
        ],
        thumb: [
            null as HTMLDivElement | null,
            {
                setThumb: (_, { ref }) => ref.current,
            },
        ],
        isSeeking: [
            false,
            {
                handleSeek: () => true,
                endSeeking: () => false,
            },
        ],
        isScrubbing: [
            false,
            {
                startScrub: () => true,
                endScrub: () => false,
            },
        ],
    }),
    selectors({
        endTimeMs: [
            (selectors) => [selectors.sessionPlayerData],
            (sessionPlayerData: import('../../../../types').SessionPlayerData) => {
                return sessionPlayerData?.durationMs ?? 0
            },
        ],

        scrubbingTime: [
            (selectors) => [selectors.thumbLeftPos, selectors.slider, selectors.sessionPlayerData],
            (
                thumbLeftPos: number,
                slider: HTMLDivElement | null,
                sessionPlayerData: import('../../../../types').SessionPlayerData
            ) => {
                if (thumbLeftPos && slider && sessionPlayerData?.durationMs) {
                    return ((thumbLeftPos + THUMB_OFFSET) / slider.offsetWidth) * sessionPlayerData.durationMs
                }
                return 0
            },
        ],
        scrubbingTimeSeconds: [
            (selectors) => [selectors.scrubbingTime],
            (scrubbingTime: number) => Math.floor(scrubbingTime / 1000),
        ],
    }),
    listeners(({ values, actions, cache }) => ({
        setSlider: () => {
            // When the slider is set (component mounted), initialize thumb position based on current timestamp
            if (values.slider && values.sessionPlayerData.durationMs) {
                const xValue =
                    ((values.currentPlayerTime ?? 0) / values.sessionPlayerData.durationMs) * values.slider.offsetWidth
                actions.setThumbLeftPos(xValue - THUMB_OFFSET, false)
            }
        },

        setCurrentTimestamp: () => {
            if (!values.slider) {
                return
            }
            // Don't update thumb position if seekbar logic is already seeking and setting the thumb position.
            // Except in one case when the updated thumb position is at the start. This happens when the user
            // scrubs to the end of the recording while playing, and then the player loop restarts the recording.
            if (!values.isSeeking) {
                const xValue =
                    ((values.currentPlayerTime ?? 0) / values.sessionPlayerData.durationMs) * values.slider.offsetWidth
                actions.setThumbLeftPos(xValue - THUMB_OFFSET, false)
            }
        },

        setThumbLeftPos: ({ thumbLeftPos, shouldSeek }) => {
            if (!values.slider) {
                return
            }
            if (shouldSeek) {
                const playerTime =
                    ((thumbLeftPos + THUMB_OFFSET) / values.slider.offsetWidth) * values.sessionPlayerData.durationMs
                actions.seekToTime(playerTime)
            }
        },
        handleSeek: async ({ newX, shouldSeek }, breakpoint) => {
            await breakpoint(5)

            const end = values.slider?.offsetWidth ?? 0
            actions.setThumbLeftPos(clamp(newX, 0, end) - THUMB_OFFSET, shouldSeek)
            actions.endSeeking()
        },
        handleMove: async ({ event }, breakpoint) => {
            if (!values.slider) {
                return
            }
            await breakpoint(10)

            actions.startScrub()
            const newX = getXPos(event) - values.cursorDiff - values.slider.getBoundingClientRect().left
            actions.handleSeek(newX, false)
        },
        handleUp: async ({ event }, breakpoint) => {
            await breakpoint(25)

            cache.disposables.dispose('seekbarListeners')

            if (values.slider) {
                const newX = getXPos(event) - values.cursorDiff - values.slider.getBoundingClientRect().left
                actions.handleSeek(newX)
            }
            actions.endScrub()
        },
        handleDown: ({ event }) => {
            if (!values.thumb) {
                return
            }

            actions.startScrub()
            const xPos = getXPos(event)
            let diffFromThumb = xPos - values.thumb.getBoundingClientRect().left - THUMB_OFFSET
            // Only a press on the thumb itself keeps the grab offset; pressing the bar next to
            // it seeks to the exact position — otherwise small seeks near the thumb go nowhere
            if (Math.abs(diffFromThumb) > THUMB_OFFSET) {
                diffFromThumb = 0
            }
            actions.setCursorDiff(diffFromThumb)

            cache.disposables.add(() => {
                document.addEventListener('touchmove', actions.handleMove, { passive: true })
                document.addEventListener('touchend', actions.handleUp)
                document.addEventListener('mousemove', actions.handleMove)
                document.addEventListener('mouseup', actions.handleUp)

                return () => {
                    document.removeEventListener('touchmove', actions.handleMove)
                    document.removeEventListener('touchend', actions.handleUp)
                    document.removeEventListener('mousemove', actions.handleMove)
                    document.removeEventListener('mouseup', actions.handleUp)
                }
            }, 'seekbarListeners')
        },
    })),
])
